import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../../pages/dashboard/LoadingSpinner';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Sector,
} from 'recharts';

const RADIAN = Math.PI / 180;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded shadow-md">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ background: `url(#${entry.gradientId})` }} />
          <span className="font-semibold text-gray-800">{entry.name}</span>
        </div>
        <p className="text-sm text-gray-600 m-0">Value: <strong>{entry.value}</strong></p>
        {entry.package && (
          <p className="text-sm text-gray-600 m-0">Package: <strong>{entry.package}</strong></p>
        )}
      </div>
    );
  }
  return null;
};

// External label with arrow for outer ring
const renderCustomLabel = (props) => {
  const {
    cx, cy, midAngle, outerRadius, percent, payload,
  } = props;

  const radius = outerRadius * 1.15;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const lineX = cx + (outerRadius + 10) * Math.cos(-midAngle * RADIAN);
  const lineY = cy + (outerRadius + 10) * Math.sin(-midAngle * RADIAN);

  const isRight = x > cx;

  if (percent < 0.02) return null;

  return (
    <g>
      <path d={`M${lineX},${lineY}L${x},${y}`} stroke="#334155" strokeWidth={1.2} fill="none" />
      <circle cx={x} cy={y} r={3} fill="#334155" />
      <rect
        x={x + (isRight ? 6 : -146)}
        y={y - 20}
        width={140}
        height={34}
        fill="white"
        rx={4}
        opacity={0.95}
      />
      <text
        x={x + (isRight ? 12 : -12)}
        y={y - 6}
        textAnchor={isRight ? 'start' : 'end'}
        fontSize={12}
        fontWeight={600}
        fill="#1e293b"
      >
        {payload.name}
      </text>
      <text
        x={x + (isRight ? 12 : -12)}
        y={y + 8}
        textAnchor={isRight ? 'start' : 'end'}
        fontSize={11}
        fill="#64748b"
      >
        {`${payload.value} (${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

// Inner label for packages
const renderInnerLabel = (props) => {
  const {
    cx, cy, midAngle, innerRadius, outerRadius, payload,
  } = props;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  const trimLabel = (label, maxLength = 12) =>
    label.length > maxLength ? label.slice(0, maxLength) + '…' : label;

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10}
      fontWeight={600}
      style={{ pointerEvents: 'auto' }}
    >
      <title>{payload.name}</title> {/* Tooltip on hover */}
      {trimLabel(payload.name)}
    </text>
  );
};


// Glow effect on hover
const renderActiveShape = ({
  cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
}) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 15}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      opacity={0.3}
    />
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  </g>
);

const DonutChart = ({ data, title = "Architecture smells by package" }) => {
  const [activeIndexInner, setActiveIndexInner] = useState(null);
  const [activeIndexOuter, setActiveIndexOuter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (data && data.length > 0) {
      setIsLoading(true);
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 500); // simulate loading/render delay
      return () => clearTimeout(timeout);
    } else {
      setIsLoading(false);
    }
  }, [data]);

  // Transform hierarchical data into two separate series
  const transformData = (hierarchicalData) => {
    // Base gradient color schemes
    const packageGradients = [
      { start: '#06b6d4', end: '#06b6d4' },
      { start: '#8b5cf6', end: '#8b5cf6' },
      { start: '#f59e0b', end: '#f59e0b' },
      { start: '#ef4444', end: '#ef4444'},
      { start: '#6366f1', end: '#6366f1'}
    ];

    const packageData = [];
    const smellData = [];

    hierarchicalData.forEach((pkg, pkgIndex) => {
      const gradient = packageGradients[pkgIndex % packageGradients.length];

      const packageTotal = pkg.children ?
        pkg.children.reduce((sum, child) => sum + (child.value || 0), 0) :
        pkg.value || 0;

      packageData.push({
        name: pkg.name,
        value: packageTotal,
        gradientId: `packageGradient${pkgIndex}`,
        gradientStart: gradient.start,
        gradientEnd: gradient.end,
        type: 'Package'
      });

      if (pkg.children) {
        pkg.children.forEach((smell, smellIndex) => {
          // Create variations of the package gradient for smells
          const smellGradientId = `smellGradient${pkgIndex}_${smellIndex}`;
          
          smellData.push({
            name: smell.name,
            value: smell.value || 0,
            gradientId: smellGradientId,
            gradientStart: gradient.start,
            gradientEnd: gradient.end,
            type: 'Smell',
            package: pkg.name
          });
        });
      }
    });

    return { packageData, smellData };
  };

  const { packageData, smellData } = transformData(data);

  // Generate gradient definitions
  const generateGradients = () => {
    const gradients = [];
    
    // Package gradients
    packageData.forEach((item, index) => {
      gradients.push(
        <defs key={`package-gradient-${index}`}>
          <radialGradient id={item.gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={item.gradientStart} stopOpacity={0.6} />
            <stop offset="50%" stopColor={item.gradientStart} stopOpacity={0.8} />
            <stop offset="100%" stopColor={item.gradientEnd} stopOpacity={1} />
          </radialGradient>
        </defs>
      );
    });

    // Smell gradients
    smellData.forEach((item, index) => {
      gradients.push(
        <defs key={`smell-gradient-${index}`}>
          <linearGradient id={item.gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={item.gradientStart} stopOpacity={0.6} />
            <stop offset="50%" stopColor={item.gradientStart} stopOpacity={0.8} />
            <stop offset="100%" stopColor={item.gradientEnd} stopOpacity={1} />
          </linearGradient>
        </defs>
      );
    });

    return gradients;
  };

  // Sample data for demonstration
  const sampleData = data || [
    {
      name: "Package A",
      children: [
        { name: "Code Smell 1", value: 15 },
        { name: "Code Smell 2", value: 25 },
        { name: "Code Smell 3", value: 10 }
      ]
    },
    {
      name: "Package B", 
      children: [
        { name: "Code Smell 4", value: 20 },
        { name: "Code Smell 5", value: 30 }
      ]
    }
  ];

  const finalData = data || sampleData;
  const { packageData: finalPackageData, smellData: finalSmellData } = transformData(finalData);


  return (
    <div className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl">
      <h5 className="mb-4 text-lg font-semibold text-gray-800">
        {title}
      </h5>
{isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
          <LoadingSpinner title={`Loading architecture smells by package...`} />
        </div>
      ) : (
      <ResponsiveContainer width="100%" height={450}>
  <PieChart>
    {generateGradients()}

    <Tooltip content={<CustomTooltip />} />

    {/* Inner Ring - Packages */}
    <Pie
      data={finalPackageData}
      cx="50%"
      cy="50%"
      innerRadius={30}
      outerRadius={100}
      labelLine={false}
      label={renderInnerLabel}
      activeIndex={activeIndexInner}
      activeShape={renderActiveShape}
      onMouseEnter={(_, index) => setActiveIndexInner(index)}
      onMouseLeave={() => setActiveIndexInner(null)}
      dataKey="value"
      nameKey="name"
    >
      {finalPackageData.map((entry, index) => (
        <Cell
          key={`cell-pkg-${index}`}
          fill={`url(#${entry.gradientId})`}
          stroke="#fff"
          strokeWidth={2}
        />
      ))}
    </Pie>

    {/* Outer Ring - Smells */}
    <Pie
      data={finalSmellData}
      cx="50%"
      cy="50%"
      innerRadius={105}
      outerRadius={175}
      labelLine={false}
      label={renderCustomLabel}
      activeIndex={activeIndexOuter}
      activeShape={renderActiveShape}
      onMouseEnter={(_, index) => setActiveIndexOuter(index)}
      onMouseLeave={() => setActiveIndexOuter(null)}
      dataKey="value"
      nameKey="name"
    >
      {finalSmellData.map((entry, index) => (
        <Cell
          key={`cell-smell-${index}`}
          fill={`url(#${entry.gradientId})`}
          stroke="#fff"
          strokeWidth={1}
        />
      ))}
    </Pie>
  </PieChart>
</ResponsiveContainer>
)}
    </div>
  );
};

export default DonutChart;